/**
 * Waterfall Chart — for variance bridges (revenue bridge, expense bridge).
 *
 * Built on Recharts stacked BarChart with an invisible base bar to create
 * the waterfall effect.
 *
 * Usage:
 *   <WaterfallChart
 *     data={[
 *       { label: "Prior Period", value: 2000000, type: "total" },
 *       { label: "Price", value: 150000, type: "increase" },
 *       { label: "Volume", value: -80000, type: "decrease" },
 *       { label: "Current Period", value: 2070000, type: "total" },
 *     ]}
 *     unit="USD"
 *   />
 */

import React, { useMemo } from 'react';
import {
  ResponsiveContainer, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Cell, LabelList,
} from 'recharts';
import { CHART_COLORS, RECHARTS_DEFAULTS, formatMetricValue } from '../../config/chartTheme';

interface WaterfallDataPoint {
  label: string;
  value: number;
  type: 'total' | 'increase' | 'decrease';
}

interface WaterfallChartProps {
  data: WaterfallDataPoint[];
  unit?: string;
  height?: number;
  showLabels?: boolean;
}

interface ComputedBar {
  label: string;
  base: number;    // Invisible base
  delta: number;   // Visible bar
  value: number;   // Original value (for tooltip)
  type: string;
  color: string;
}

const CustomTooltip = ({ active, payload, unit }: any) => {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload;
  if (!data) return null;
  return (
    <div style={RECHARTS_DEFAULTS.tooltipContentStyle}>
      <div style={RECHARTS_DEFAULTS.tooltipLabelStyle}>{data.label}</div>
      <div style={{ color: data.color, fontWeight: 600 }}>
        {data.type === 'total'
          ? formatMetricValue(data.value, unit || 'USD')
          : `${data.value >= 0 ? '+' : ''}${formatMetricValue(data.value, unit || 'USD')}`
        }
      </div>
    </div>
  );
};

export const WaterfallChart: React.FC<WaterfallChartProps> = ({
  data, unit = 'USD', height = 300, showLabels = true,
}) => {
  const computed: ComputedBar[] = useMemo(() => {
    const bars: ComputedBar[] = [];
    let running = 0;

    for (const d of data) {
      if (d.type === 'total') {
        bars.push({
          label: d.label,
          base: 0,
          delta: d.value,
          value: d.value,
          type: d.type,
          color: CHART_COLORS.accent,
        });
        running = d.value;
      } else if (d.value >= 0) {
        // Increase: bar sits on top of running total
        bars.push({
          label: d.label,
          base: running,
          delta: d.value,
          value: d.value,
          type: 'increase',
          color: CHART_COLORS.positive,
        });
        running += d.value;
      } else {
        // Decrease: bar hangs down from running total
        running += d.value;
        bars.push({
          label: d.label,
          base: running,
          delta: Math.abs(d.value),
          value: d.value,
          type: 'decrease',
          color: CHART_COLORS.negative,
        });
      }
    }
    return bars;
  }, [data]);

  const renderLabel = (props: any) => {
    if (!showLabels) return null;
    const { x, y, width, index } = props;
    const bar = computed[index];
    if (!bar) return null;
    const displayVal = bar.type === 'total'
      ? formatMetricValue(bar.value, unit)
      : `${bar.value >= 0 ? '+' : ''}${formatMetricValue(bar.value, unit)}`;
    return (
      <text
        x={x + width / 2}
        y={y - 6}
        fill={CHART_COLORS.textSecondary}
        textAnchor="middle"
        fontSize={10}
        fontFamily="var(--font-sans)"
      >
        {displayVal}
      </text>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={computed}>
        <CartesianGrid {...RECHARTS_DEFAULTS.gridProps} />
        <XAxis dataKey="label" {...RECHARTS_DEFAULTS.xAxisProps} />
        <YAxis
          {...RECHARTS_DEFAULTS.yAxisProps}
          tickFormatter={(v: number) => formatMetricValue(v, unit)}
        />
        <Tooltip content={<CustomTooltip unit={unit} />} cursor={RECHARTS_DEFAULTS.tooltipCursor} />

        {/* Invisible base bar */}
        <Bar dataKey="base" stackId="waterfall" fill="transparent" />

        {/* Visible delta bar */}
        <Bar dataKey="delta" stackId="waterfall" radius={[4, 4, 0, 0]}>
          {computed.map((bar, i) => (
            <Cell key={i} fill={bar.color} />
          ))}
          <LabelList content={renderLabel} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default WaterfallChart;
